'use client';

import { Toast, Toaster, createToaster } from '@ark-ui/react/toast';
import { CheckCircleIcon, CloseCircleIcon, WarningIcon, InfoCircleIcon, SparklesIcon, CloseIcon } from '@unitfield/ui/icons';

import { cn } from '../../lib/utils';

// Icon mapping for toast types
const toastIcons = {
  success: CheckCircleIcon,
  error: CloseCircleIcon,
  warning: WarningIcon,
  info: InfoCircleIcon,
  primary: SparklesIcon,
};

const toastStyles = {
  success: {
    icon: 'bg-status-success-0 text-status-success-1',
    border: 'border-status-success-1/25',
  },
  error: {
    icon: 'bg-status-danger-0 text-status-danger-1',
    border: 'border-status-danger-1/25',
  },
  warning: {
    icon: 'bg-status-warning-0 text-status-warning-1',
    border: 'border-status-warning-1/25',
  },
  info: {
    icon: 'bg-status-info-0 text-status-info-1',
    border: 'border-status-info-1/25',
  },
  primary: {
    icon: 'bg-primary/15 text-primary',
    border: 'border-primary/25',
  },
};

// Create toaster instance
export const toaster = createToaster({
  placement: 'bottom-end',
  overlap: true, // Enable stacked/overlapping toasts
  gap: 8,
  duration: 4000,
  max: 5,
  offsets: {
    top: '0',
    bottom: '1rem',
    left: '1rem',
    right: '1rem',
  },
});

function scheduleToastCreate(options: Parameters<typeof toaster.create>[0]) {
  if (typeof window === 'undefined') {
    toaster.create(options);
    return;
  }

  window.setTimeout(() => {
    toaster.create(options);
  }, 0);
}

// Toaster Provider Component
export function ArkToaster() {
  return (
    <Toaster toaster={toaster}>
      {(toast) => {
        const IconComponent = toastIcons[toast.type as keyof typeof toastIcons] || InfoCircleIcon;
        const style = toastStyles[toast.type as keyof typeof toastStyles] || toastStyles.info;
        
        return (
          <Toast.Root
            key={toast.id}
            className={cn(
              'relative w-[min(343px,calc(100vw-2rem))] max-w-[calc(100vw-2rem)] overflow-hidden rounded-xl border border-solid bg-surface-outer shadow-sm',
              style.border,
            )}
          >
            <div className="flex items-center gap-3 px-3.5 py-3">
              <div className={cn('grid size-8 shrink-0 place-items-center rounded-full [&>svg]:size-4', style.icon)}>
                <IconComponent size={24} />
              </div>
              <div className="flex-1 min-w-0">
                <Toast.Title className="text-sm font-semibold leading-5 tracking-[-0.2px] text-foreground">
                  {toast.title}
                </Toast.Title>
                {toast.description && (
                  <Toast.Description className="mt-0.5 text-sm leading-[18px] tracking-[-0.078px] text-muted-foreground">
                    {toast.description}
                  </Toast.Description>
                )}
              </div>
              {toast.action ? (
                <Toast.ActionTrigger className="inline-flex h-8 shrink-0 cursor-pointer items-center rounded-full bg-surface-control px-3.5 text-[13px] font-semibold leading-none text-foreground transition-colors hover:bg-surface-selected focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                  {toast.action.label}
                </Toast.ActionTrigger>
              ) : null}
              <Toast.CloseTrigger className="flex-shrink-0 cursor-pointer text-foreground opacity-70 transition-opacity hover:opacity-100">
                <CloseIcon size={16} />
              </Toast.CloseTrigger>
            </div>
          </Toast.Root>
        );
      }}
    </Toaster>
  );
}

// Toast helper functions
export const customToast = {
  success: (title: string, description?: string) => {
    scheduleToastCreate({
      title,
      description,
      type: 'success',
    });
  },
  error: (title: string, description?: string) => {
    scheduleToastCreate({
      title,
      description,
      type: 'error',
    });
  },
  warning: (title: string, description?: string) => {
    scheduleToastCreate({
      title,
      description,
      type: 'warning',
    });
  },
  info: (title: string, description?: string) => {
    scheduleToastCreate({
      title,
      description,
      type: 'info',
    });
  },
  primary: (title: string, description?: string) => {
    scheduleToastCreate({
      title,
      description,
      type: 'primary',
    });
  },
  /** A neutral confirmation with one action ("Unpinned Rent Roll · Undo"). */
  undoable: (title: string, onUndo: () => void, actionLabel = 'Undo', duration = 6000) => {
    scheduleToastCreate({
      title,
      type: 'info',
      duration,
      action: { label: actionLabel, onClick: onUndo },
    });
  },
};

// Alias for backward compatibility
export const toast = customToast;
